import React, { useEffect, useState } from "react";
import { useStyles } from "./style";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useAuth0 } from "@auth0/auth0-react";
import { inRange, filter, some } from "lodash";
import { Box, useTheme, Typography } from "@mui/material";
import ColumnConfigHead from "./columnsHeaders/dateHeaders";
import ColumnConfigHead2 from "./columnsHeaders/yieldHeaders";
import ColumnConfigHead3 from "./columnsHeaders/takaHeaders";
import ColumnConfigBody from "./columnsBody/dateColumns";
import ColumnConfigBody2 from "./columnsBody/yieldColumns";
import ColumnConfigBody3 from "./columnsBody/takaColumns";
import CustomPagination from "./customPagination/CustomPagination";
import ColumnSort from "./utils/columnSort";
import { commonMinWidthFunction } from "./utils/commonMinWidth";
import { applyFilter, applyStageFilter } from "./utils/lotnumberfilter";
import { descendingComparator } from "./utils/descendingComparator";
import LoadingSpinner from "components/loadingSpinner";
import constants from "../../../../constants/constants.json";
import { getToken } from "utils/loadAuth";
import { apis } from "config/urls";
import { getAPI } from "utils/http";

const Table = ({
  selectedTaka = "Taka 3.1",
  selectedDates,
  filterBy,
  showHideList,
  setCurrentFilteredData,
  setAllRecords,
  setAllData,
  searchChange,
  setSearchChange,
  selectedStages,
  clearFilters,
  setClearFilters,
  stageFilter,
}) => {
  const theme = useTheme();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const isDarkMode = useTheme().palette.mode === "dark";
  const classes = useStyles({ theme, isDarkMode });

  //Sort
  const [sortOrder, setSortOrder] = useState("initial");
  const [sortBy, setSortBy] = useState("");

  const [lotStatusData, setLotStatusData] = useState([]);

  const [totalPages, setTotalPages] = useState(0);
  const [totalFilterPages, setTotalFilterPages] = useState(0);

  const [dataPerPageState, setDataPerPageState] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [currentRecords, setCurrentRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadData = React.useCallback(async () => {
    try {
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (token) {
        let version;
        if (selectedTaka === constants.taka41a) {
          version = "v41a";
        } else if (selectedTaka === constants.taka41c) {
          version = "v41c";
        } else {
          version = "v31";
        }
        const { data: result } = await getAPI(
          apis.amkorLotDataView,
          {
            version,
          },
          token
        );
        const sortedData = result.sort(
          (a, b) => -descendingComparator(a, b, "LotNumber")
        );
        setSortOrder("initial");
        toggleSortOrder("LotNumber");
        setLotStatusData(sortedData);
        setCurrentRecords(sortedData);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log("Error: ", error.message);
    }
  }, [getAccessTokenSilently, loginWithRedirect, selectedTaka]);

  useEffect(() => {
    setIsLoading(true);
    loadData();
  }, [loadData]);

  useEffect(() => {
    const stageData = applyStageFilter({
      filter: stageFilter,
      data: lotStatusData,
    });
    setCurrentRecords(stageData);
  }, [lotStatusData, stageFilter]);

  useEffect(() => {
    if (clearFilters) {
      const copiedData = [...lotStatusData];
      setCurrentRecords(copiedData);
      setCurrentPage(1);
    }
    setClearFilters(false);
  }, [clearFilters, lotStatusData, setClearFilters]);

  useEffect(() => {
    let copiedData = [...lotStatusData];
    if (filterBy !== "") {
      const filteredData = applyFilter(lotStatusData, filterBy);
      copiedData = [...filteredData];
    }
    let startDate = new Date();
    let endDate = new Date();
    if (selectedDates.length === 2) {
      startDate = new Date(selectedDates[0]);
      endDate = new Date(selectedDates[1]);

      const result = filter(copiedData, (item) => {
        const dateFields = {
          dieReceiptDate: new Date(item?.DieReceipt),
          bumpInDate: new Date(item?.BumpIn),
          bumpOutDate: new Date(item?.BumpOut),
          probeInDate: new Date(item?.ProbeIn),
          probeOutDate: new Date(item?.ProbeOut),
          assemblyInDate: new Date(item?.AssemblyIn),
          assemblyOutDate: new Date(item?.AssemblyOut),
          testInDate: new Date(item?.TestIn),
          testOutDate: new Date(item?.TestOut),
          shipOutDate: new Date(item?.ShipOut),
        };

        const filterResult = some([
          selectedStages.dieReceipt &&
            inRange(dateFields.dieReceiptDate, startDate, endDate),
          selectedStages.bump &&
            (inRange(dateFields.bumpInDate, startDate, endDate) ||
              inRange(dateFields.bumpOutDate, startDate, endDate)),
          selectedStages.probe &&
            (inRange(dateFields.probeInDate, startDate, endDate) ||
              inRange(dateFields.probeOutDate, startDate, endDate)),
          selectedStages.assembly &&
            (inRange(dateFields.assemblyInDate, startDate, endDate) ||
              inRange(dateFields.assemblyOutDate, startDate, endDate)),
          selectedStages.test &&
            (inRange(dateFields.testInDate, startDate, endDate) ||
              inRange(dateFields.testOutDate, startDate, endDate)),
          selectedStages.ship &&
            inRange(dateFields.shipOutDate, startDate, endDate),
        ]);

        return filterResult;
      });
      setCurrentRecords(result);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, selectedStages, filterBy]);

  useEffect(() => {
    setCurrentFilteredData(currentPageData);
  }, [currentPageData, setCurrentFilteredData]);

  useEffect(() => {
    setAllRecords(currentRecords);
  }, [currentRecords, setAllRecords]);

  useEffect(() => {
    const copiedRecords = [...currentRecords];
    const sortedDataValue = ColumnSort(
      currentRecords,
      sortBy,
      sortOrder,
      copiedRecords
    );

    setCurrentRecords(sortedDataValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  useEffect(() => {
    setAllData(lotStatusData);
  }, [setAllData, lotStatusData]);

  useEffect(() => {
    const copiedCurrentRecords = [...currentRecords];
    const startIndex = (currentPage - 1) * dataPerPageState;
    const endIndex = Math.min(
      startIndex + dataPerPageState,
      copiedCurrentRecords.length
    );
    const data = copiedCurrentRecords.slice(startIndex, endIndex);
    setCurrentPageData(data);
    setTotalFilterPages(
      Math.ceil(copiedCurrentRecords.length / dataPerPageState)
    );
    setTotalPages(copiedCurrentRecords.length);
  }, [currentPage, dataPerPageState, currentRecords]);

  const toggleSortOrder = (columnName) => {
    setSortBy(columnName);
    setSortOrder((prevOrder) => {
      if (prevOrder === "asc") {
        return "desc";
      } else if (prevOrder === "desc") {
        return "asc";
      } else {
        return "asc";
      }
    });
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const onRowsPerPageChange = (e) => {
    setCurrentPage(1);
    setDataPerPageState(parseInt(e.target.value, 10));
  };

  const paginationFilterLogic = filterBy && filterBy !== "";

  let content;

  if (isLoading) {
    content = <LoadingSpinner loadingMessage={constants.loading} />;
  } else if (currentPageData && currentPageData.length > 0) {
    content = currentPageData.map((data, i) => (
      <Box
        className={classes.lotsContentWrapper}
        key={data.LotNumber}
        sx={{
          borderBottom: `1px solid ${isDarkMode ? "#FFFFFF" : "#5b5d6e"}`,
          minWidth: commonMinWidthFunction(
            showHideList.yield,
            showHideList.dates,
            showHideList.duration,
            selectedTaka
          ),
        }}
      >
        <ColumnConfigBody
          showHideList={showHideList}
          classes={classes}
          data={data}
        />
        <ColumnConfigBody2
          showHideList={showHideList}
          classes={classes}
          data={data}
          selectedTaka={selectedTaka}
        />
        <ColumnConfigBody3
          showHideList={showHideList}
          classes={classes}
          data={data}
          selectedTaka={selectedTaka}
        />
      </Box>
    ));
  } else {
    content = (
      <Box className={classes.noRowsWrapper}>
        <Typography sx={{ color: theme.palette.text.primary }} variant="h7">
          {constants.noResults}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.paginationContainer}>
      <Box
        data-testid="table"
        className={clsx(
          classes.lotsTableContainer,
          classes.themeBackgroundColor
        )}
      >
        <Box
          className={clsx(
            classes.lotsTableHeadersWrapper,
            classes.themeBackgroundColor
          )}
        >
          <Box
            className={clsx(
              classes.headersContainer,
              classes.themeBackgroundColor,
              classes.themeColor
            )}
            sx={{
              minWidth: commonMinWidthFunction(
                showHideList.yield,
                showHideList.dates,
                showHideList.duration,
                selectedTaka
              ),
              borderBottom: `1px solid ${isDarkMode ? "#FFFFFF" : "#5b5d6e"}`,
            }}
          >
            <ColumnConfigHead
              showHideList={showHideList}
              classes={classes}
              toggleSortOrder={toggleSortOrder}
              sortOrder={sortOrder}
              sortBy={sortBy}
            />
            <ColumnConfigHead2
              showHideList={showHideList}
              classes={classes}
              toggleSortOrder={toggleSortOrder}
              sortOrder={sortOrder}
              sortBy={sortBy}
              selectedTaka={selectedTaka}
            />
            <ColumnConfigHead3
              showHideList={showHideList}
              classes={classes}
              toggleSortOrder={toggleSortOrder}
              sortOrder={sortOrder}
              sortBy={sortBy}
              selectedTaka={selectedTaka}
            />
          </Box>

          {content}
        </Box>
      </Box>

      <CustomPagination
        classes={classes}
        paginationFilterLogic={paginationFilterLogic}
        totalFilterPages={totalFilterPages}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        filteredData={currentPageData}
        lotStatusData={lotStatusData}
        dataPerPageState={dataPerPageState}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
};

Table.propTypes = {
  selectedDates: PropTypes.array,
  filterBy: PropTypes.string,
  showHideList: PropTypes.object,
  setCurrentFilteredData: PropTypes.func,
  setAllRecords: PropTypes.func,
  setAllData: PropTypes.func,
  searchChange: PropTypes.bool,
  setSearchChange: PropTypes.func,
  selectedStages: PropTypes.object,
  clearFilters: PropTypes.bool,
  setClearFilters: PropTypes.func,
  stageFilter: PropTypes.object,
  selectedTaka: PropTypes.string,
};

export default Table;
